/**
 * Decision Room → Proposal section, SELLER side (2026-09-07, flag `proposals`).
 *
 * A proposal is a section of the room, never a separate document: an
 * ordered list of blocks (narrative, the call's trailer, a quote table,
 * a room document, media) plus the line items behind the quote. The
 * server owns the totals; this editor shows a live preview while the
 * seller types and re-renders from the saved response.
 *
 * Lifecycle: draft → shared (visible to the buyer; sharing supersedes
 * any other shared proposal in the room and turns the section on) →
 * accepted (frozen — duplicate to iterate). "Templates" in v1 are
 * duplicates of a past proposal.
 *
 * Backend: routes/proposals.js. Seller-side surface, --room-* tokens.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest, moneyLabel, parseMoneyToCents, formatWhen, formatDate } from '../utils/apiRequest';
import { computeTotals } from '../utils/proposalTotals';

const STATUS_CHIP = {
  draft:      { bg: '#f1f5f9', ink: '#475569', label: 'Draft' },
  shared:     { bg: '#eff6ff', ink: '#1e40af', label: 'Shared with buyer' },
  accepted:   { bg: '#ecfdf5', ink: '#065f46', label: 'Accepted' },
  superseded: { bg: '#fef3c7', ink: '#92400e', label: 'Superseded' },
};
const BLOCK_LABEL = { narrative: 'Narrative', trailer: 'Call trailer', quote: 'Quote table', doc: 'Document', media: 'Media' };

function StatusChip({ status }) {
  const s = STATUS_CHIP[status] || STATUS_CHIP.draft;
  return <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: s.bg, color: s.ink, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

const key = () => Math.random().toString(36).slice(2);

function toDraftBlocks(blocks) {
  return (blocks || []).map((b) => ({ key: b.id || key(), blockType: b.blockType, title: b.title || '', config: { ...(b.config || {}) } }));
}
function toApiBlocks(draft) {
  return draft.map((b) => ({ blockType: b.blockType, title: b.title.trim() || null, config: b.config }));
}
function toDraftLines(items) {
  return (items || []).map((li) => ({
    key: li.id || key(), productId: li.productId || '', description: li.description || '',
    quantity: String(li.quantity ?? 1), unitPrice: ((li.unitPriceCents ?? 0) / 100).toFixed(2),
    discountPct: String(li.discountPct ?? 0), taxRatePct: String(li.taxRatePct ?? 0),
    isOptional: !!li.isOptional, selected: li.selected !== false, optionGroup: li.optionGroup || '',
  }));
}
function toApiLines(draft) {
  return draft.map((l) => ({
    productId: l.productId || null, description: l.description, quantity: Number(l.quantity),
    unitPriceCents: parseMoneyToCents(l.unitPrice), discountPct: Number(l.discountPct) || 0, taxRatePct: Number(l.taxRatePct) || 0,
    isOptional: l.isOptional, selected: l.selected, optionGroup: l.optionGroup.trim() || null,
  }));
}

export default function ProposalEditor({ data, authToken, spaceId, onReload }) {
  const [list, setList] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    const r = await apiRequest(`/api/proposals/room/${spaceId}`, { token: authToken });
    const items = r.proposals || [];
    setList(items);
    return items;
  }, [authToken, spaceId]);

  const loadOne = useCallback(async (id) => {
    const r = await apiRequest(`/api/proposals/${id}`, { token: authToken });
    setProposal(r.proposal);
    return r.proposal;
  }, [authToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [items, p] = await Promise.all([loadList(), apiRequest('/api/products', { token: authToken })]);
        if (cancelled) return;
        setProducts(p.products || []);
        const first = items.find((x) => x.status === 'shared') || items.find((x) => x.status === 'draft') || items[0];
        if (first) { setSelectedId(first.id); await loadOne(first.id); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [loadList, loadOne, authToken]);

  const select = async (id) => {
    setSelectedId(id);
    setProposal(null);
    try { await loadOne(id); } catch (err) { setError(err.message); }
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await apiRequest('/api/proposals', { token: authToken, method: 'POST', body: { buyerSpaceId: spaceId } });
      await loadList();
      await select(r.proposal.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const refreshAfter = async (id) => { await loadList(); await loadOne(id); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {error && <div role="alert" style={alertStyle}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {list === null ? (
          <span style={{ color: 'var(--room-text-muted, #64748b)' }}>Loading…</span>
        ) : list.length > 0 && (
          <select value={selectedId || ''} onChange={(e) => select(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 260 }} aria-label="Proposal">
            {list.map((p) => <option key={p.id} value={p.id}>{p.title} — {STATUS_CHIP[p.status] ? STATUS_CHIP[p.status].label : p.status}</option>)}
          </select>
        )}
        <button type="button" onClick={create} disabled={busy} style={ghostBtn}>+ New proposal</button>
      </div>

      {list && list.length === 0 && (
        <div style={cardStyle}>
          <p style={{ margin: 0, color: 'var(--room-text-muted, #64748b)' }}>
            No proposal in this room yet. Start one, add the call's trailer and a quote, then share it. The buyer sees it as a section of this room.
          </p>
        </div>
      )}

      {selectedId && !proposal && list && list.length > 0 && <div style={{ color: 'var(--room-text-muted, #64748b)' }}>Loading proposal…</div>}

      {proposal && (
        <ProposalDetail
          key={proposal.id}
          proposal={proposal}
          data={data}
          products={products}
          authToken={authToken}
          onChanged={() => refreshAfter(proposal.id)}
          onDuplicated={async (copy) => { await loadList(); await select(copy.id); }}
          onRoomChanged={onReload}
          setError={setError}
        />
      )}
    </div>
  );
}

function ProposalDetail({ proposal, data, products, authToken, onChanged, onDuplicated, onRoomChanged, setError }) {
  const frozen = proposal.status === 'accepted' || proposal.status === 'superseded';
  const [title, setTitle] = useState(proposal.title);
  const [validUntil, setValidUntil] = useState(proposal.validUntil ? String(proposal.validUntil).slice(0, 10) : '');
  const [notes, setNotes] = useState(proposal.notes || '');
  const [blocks, setBlocks] = useState(() => toDraftBlocks(proposal.blocks));
  const [lines, setLines] = useState(() => toDraftLines(proposal.lineItems));
  const [blocksDirty, setBlocksDirty] = useState(false);
  const [linesDirty, setLinesDirty] = useState(false);
  const [saving, setSaving] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [envelopeId, setEnvelopeId] = useState('');
  const [busy, setBusy] = useState(false);

  const trailers = useMemo(() => (data.trailers || []).filter((t) => t.status === 'completed'), [data.trailers]);
  const docs = useMemo(() => (data.docs || []).filter((d) => !d.prospectHidden), [data.docs]);
  const liveTotals = useMemo(() => computeTotals(toApiLines(lines)), [lines]);

  const patch = async (body) => {
    setSaving('meta');
    try {
      await apiRequest(`/api/proposals/${proposal.id}`, { token: authToken, method: 'PATCH', body });
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const saveBlocks = async () => {
    setSaving('blocks');
    setError(null);
    try {
      await apiRequest(`/api/proposals/${proposal.id}/blocks`, { token: authToken, method: 'PUT', body: { blocks: toApiBlocks(blocks) } });
      setBlocksDirty(false);
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const saveLines = async () => {
    setSaving('lines');
    setError(null);
    try {
      await apiRequest(`/api/proposals/${proposal.id}/line-items`, { token: authToken, method: 'PUT', body: { lineItems: toApiLines(lines) } });
      setLinesDirty(false);
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const share = async () => {
    if (blocksDirty || linesDirty) { setError('Save your changes before sharing.'); return; }
    if (!window.confirm('Share this proposal with the buyer? Any other shared proposal in this room is superseded, and the Proposal section is turned on for the prospect.')) return;
    await patch({ status: 'shared' });
    onRoomChanged();
  };

  const duplicate = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await apiRequest(`/api/proposals/${proposal.id}/duplicate`, { token: authToken, method: 'POST', body: {} });
      await onDuplicated(r.proposal);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadEngagement = async () => {
    if (engagement) { setEngagement(null); return; }
    try {
      const r = await apiRequest(`/api/proposals/${proposal.id}/engagement`, { token: authToken });
      setEngagement(r.engagement || { viewers: [], blocks: [] });
    } catch (err) {
      setError(err.message);
    }
  };

  const trackEnvelope = async (e) => {
    e.preventDefault();
    if (!envelopeId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/api/proposals/${proposal.id}/esign/track`, { token: authToken, method: 'POST', body: { vendor: 'docusign', vendorEnvelopeId: envelopeId.trim(), subject: proposal.title } });
      setEnvelopeId('');
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // ─── Block helpers ──────────────────────────────────────────────
  const updateBlock = (k, patchObj) => { setBlocks((prev) => prev.map((b) => (b.key === k ? { ...b, ...patchObj } : b))); setBlocksDirty(true); };
  const updateBlockConfig = (k, cfg) => { setBlocks((prev) => prev.map((b) => (b.key === k ? { ...b, config: { ...b.config, ...cfg } } : b))); setBlocksDirty(true); };
  const moveBlock = (idx, dir) => { setBlocks((prev) => { const n = [...prev]; const j = idx + dir; if (j < 0 || j >= n.length) return prev; [n[idx], n[j]] = [n[j], n[idx]]; return n; }); setBlocksDirty(true); };
  const removeBlock = (k) => { setBlocks((prev) => prev.filter((b) => b.key !== k)); setBlocksDirty(true); };
  const addBlock = (type) => {
    const base = { key: key(), blockType: type, title: '', config: {} };
    if (type === 'narrative') base.config = { markdown: '' };
    if (type === 'trailer') base.config = { demoId: trailers[0] ? trailers[0].id : '' };
    if (type === 'doc') base.config = { docId: docs[0] ? docs[0].id : '' };
    if (type === 'media') base.config = { url: '' };
    if (type === 'quote') base.config = { showTax: false };
    setBlocks((prev) => [...prev, base]);
    setBlocksDirty(true);
  };

  // ─── Line helpers ───────────────────────────────────────────────
  const updateLine = (k, patchObj) => { setLines((prev) => prev.map((l) => (l.key === k ? { ...l, ...patchObj } : l))); setLinesDirty(true); };
  const pickProduct = (k, productId) => {
    const p = products.find((x) => x.id === productId);
    updateLine(k, p
      ? { productId, description: p.name + (p.unitLabel ? ` (per ${p.unitLabel})` : ''), unitPrice: (p.unitPriceCents / 100).toFixed(2) }
      : { productId: '' });
  };
  const addLine = () => { setLines((prev) => [...prev, { key: key(), productId: '', description: '', quantity: '1', unitPrice: '0.00', discountPct: '0', taxRatePct: '0', isOptional: false, selected: true, optionGroup: '' }]); setLinesDirty(true); };
  const removeLine = (k) => { setLines((prev) => prev.filter((l) => l.key !== k)); setLinesDirty(true); };

  const hasQuoteBlock = blocks.some((b) => b.blockType === 'quote');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            type="text" value={title} maxLength={200} disabled={frozen} aria-label="Proposal title"
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => { if (title.trim() && title.trim() !== proposal.title) patch({ title: title.trim() }); }}
            style={{ ...inputStyle, flex: 1, minWidth: 220, fontSize: 18, fontWeight: 700 }}
          />
          <StatusChip status={proposal.status} />
          {proposal.esignStatus && <span style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>e-sign: {proposal.esignStatus}</span>}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap', marginTop: 12 }}>
          <label style={labelStyle}>Valid until <span style={hint}>(optional)</span>
            <input type="date" value={validUntil} disabled={frozen} onChange={(e) => setValidUntil(e.target.value)}
              onBlur={() => { const cur = proposal.validUntil ? String(proposal.validUntil).slice(0, 10) : ''; if (validUntil !== cur) patch({ validUntil: validUntil || null }); }}
              style={{ ...inputStyle, width: 180 }} />
          </label>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {proposal.status === 'draft' && <button type="button" onClick={share} disabled={saving !== null || busy} style={primaryBtn}>Share with buyer</button>}
            <button type="button" onClick={duplicate} disabled={busy} style={ghostBtn}>Duplicate</button>
            <button type="button" onClick={loadEngagement} style={ghostBtn}>{engagement ? 'Hide engagement' : 'Engagement'}</button>
          </div>
        </div>
        {proposal.status === 'accepted' && (
          <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: '#ecfdf5', color: '#065f46', fontSize: 13 }}>
            Accepted {formatWhen(proposal.acceptedAt)}{proposal.acceptedByName ? ` by ${proposal.acceptedByName}` : ''}{proposal.acceptedByEmail ? ` (${proposal.acceptedByEmail})` : ''}. This proposal is frozen — duplicate it to make changes.
          </div>
        )}
        {proposal.status === 'superseded' && (
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>A newer proposal was shared in this room; this one is kept for the record.</div>
        )}
        <label style={{ ...labelStyle, marginTop: 12 }}>Internal notes <span style={hint}>(never shown to the buyer)</span>
          <textarea value={notes} rows={2} maxLength={4000} disabled={frozen} onChange={(e) => setNotes(e.target.value)}
            onBlur={() => { if (notes !== (proposal.notes || '')) patch({ notes: notes || null }); }} style={{ ...inputStyle, resize: 'vertical' }} />
        </label>
      </div>

      {engagement && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Who has looked</div>
          {engagement.viewers.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>Nobody has opened this proposal yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
              {engagement.viewers.map((v) => (
                <div key={v.email || v.name} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <strong>{v.name || v.email}</strong>
                  <span style={{ color: 'var(--room-text-muted, #64748b)' }}>{v.views} view{v.views === 1 ? '' : 's'} · last {formatWhen(v.lastSeenAt)}{v.accepted ? ' · accepted' : ''}</span>
                </div>
              ))}
            </div>
          )}
          {engagement.blocks && engagement.blocks.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>
              Time on blocks: {engagement.blocks.map((b) => {
                const blk = (proposal.blocks || []).find((x) => x.id === b.blockId);
                return `${blk ? (blk.title || BLOCK_LABEL[blk.blockType]) : 'block'} ${Math.round(b.seconds)}s`;
              }).join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* ─── Blocks ─────────────────────────────────────────────── */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 16, flex: 1 }}>Content</h3>
          {!frozen && blocksDirty && <button type="button" onClick={saveBlocks} disabled={saving !== null} style={primaryBtn}>{saving === 'blocks' ? 'Saving…' : 'Save content'}</button>}
        </div>
        {blocks.length === 0 && <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)', marginBottom: 10 }}>Nothing here yet. Add a short narrative, the call's trailer and a quote table.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {blocks.map((b, idx) => (
            <div key={b.key} style={{ border: '1px solid var(--room-border-soft, #f1f5f9)', borderRadius: 12, padding: 12, background: 'var(--room-surface, #f8fafc)' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--room-text-subtle, #94a3b8)' }}>{BLOCK_LABEL[b.blockType]}</span>
                <input type="text" value={b.title} maxLength={200} disabled={frozen} placeholder="Heading (optional)" onChange={(e) => updateBlock(b.key, { title: e.target.value })} style={{ ...inputStyle, flex: 1 }} />
                {!frozen && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" onClick={() => moveBlock(idx, -1)} disabled={idx === 0} style={iconBtn} title="Move up">↑</button>
                    <button type="button" onClick={() => moveBlock(idx, 1)} disabled={idx === blocks.length - 1} style={iconBtn} title="Move down">↓</button>
                    <button type="button" onClick={() => removeBlock(b.key)} style={{ ...iconBtn, color: '#dc2626' }} title="Remove">×</button>
                  </div>
                )}
              </div>
              {b.blockType === 'narrative' && (
                <textarea value={b.config.markdown || ''} rows={5} maxLength={20000} disabled={frozen} placeholder="Write in plain text or markdown. What did we hear, what are we proposing, why now."
                  onChange={(e) => updateBlockConfig(b.key, { markdown: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} />
              )}
              {b.blockType === 'trailer' && (
                trailers.length === 0 ? <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>No completed trailer in this room yet — attach one from Demos first.</div> : (
                  <select value={b.config.demoId || ''} disabled={frozen} onChange={(e) => updateBlockConfig(b.key, { demoId: e.target.value })} style={inputStyle}>
                    <option value="">Pick a trailer…</option>
                    {trailers.map((t) => <option key={t.id} value={t.id}>{t.prospectName || t.filename} — {formatDate(t.completedAt || t.createdAt)}</option>)}
                  </select>
                )
              )}
              {b.blockType === 'doc' && (
                docs.length === 0 ? <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>No buyer-visible document in this room yet.</div> : (
                  <select value={b.config.docId || ''} disabled={frozen} onChange={(e) => updateBlockConfig(b.key, { docId: e.target.value })} style={inputStyle}>
                    <option value="">Pick a document…</option>
                    {docs.map((d) => <option key={d.id} value={d.id}>{d.displayTitle || d.filename}</option>)}
                  </select>
                )
              )}
              {b.blockType === 'media' && (
                <input type="url" value={b.config.url || ''} disabled={frozen} placeholder="https://… (Loom, YouTube embed, or a direct .mp4)" onChange={(e) => updateBlockConfig(b.key, { url: e.target.value })} style={inputStyle} />
              )}
              {b.blockType === 'quote' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--room-text-body, #334155)' }}>
                  <input type="checkbox" checked={!!b.config.showTax} disabled={frozen} onChange={(e) => updateBlockConfig(b.key, { showTax: e.target.checked })} /> Show tax as its own line
                  <span style={hint}>· the table below is what renders here</span>
                </label>
              )}
            </div>
          ))}
        </div>
        {!frozen && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            {['narrative', 'trailer', 'quote', 'doc', 'media'].map((t) => (
              <button key={t} type="button" onClick={() => addBlock(t)} disabled={blocks.length >= 30 || (t === 'quote' && hasQuoteBlock)} style={ghostBtn}>+ {BLOCK_LABEL[t]}</button>
            ))}
          </div>
        )}
      </div>

      {/* ─── Quote lines ────────────────────────────────────────── */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 16, flex: 1 }}>Quote</h3>
          {!hasQuoteBlock && <span style={hint}>Add a "Quote table" block above so the buyer sees these lines.</span>}
          {!frozen && linesDirty && <button type="button" onClick={saveLines} disabled={saving !== null} style={primaryBtn}>{saving === 'lines' ? 'Saving…' : 'Save quote'}</button>}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 760, fontSize: 13 }}>
            <thead>
              <tr style={{ color: 'var(--room-text-muted, #64748b)' }}>
                <th style={th}>Product</th><th style={th}>Description</th><th style={{ ...th, width: 70 }}>Qty</th><th style={{ ...th, width: 110 }}>Unit price</th>
                <th style={{ ...th, width: 70 }}>Disc %</th><th style={{ ...th, width: 70 }}>Tax %</th><th style={{ ...th, width: 90 }}>Optional</th><th style={{ ...th, textAlign: 'right', width: 110 }}>Line</th>{!frozen && <th style={th} />}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.key} style={{ borderTop: '1px solid var(--room-border-soft, #f1f5f9)' }}>
                  <td style={td}>
                    <select value={l.productId} disabled={frozen} onChange={(e) => pickProduct(l.key, e.target.value)} style={{ ...inputStyle, minWidth: 140 }}>
                      <option value="">Custom</option>
                      {products.filter((p) => !p.archivedAt || p.id === l.productId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td style={td}><input type="text" value={l.description} maxLength={500} disabled={frozen} onChange={(e) => updateLine(l.key, { description: e.target.value })} style={{ ...inputStyle, minWidth: 180 }} /></td>
                  <td style={td}><input type="number" min={0} step="0.01" value={l.quantity} disabled={frozen} onChange={(e) => updateLine(l.key, { quantity: e.target.value })} style={inputStyle} /></td>
                  <td style={td}><input type="text" inputMode="decimal" value={l.unitPrice} disabled={frozen} onChange={(e) => updateLine(l.key, { unitPrice: e.target.value })} style={inputStyle} /></td>
                  <td style={td}><input type="number" min={0} max={100} step="0.01" value={l.discountPct} disabled={frozen} onChange={(e) => updateLine(l.key, { discountPct: e.target.value })} style={inputStyle} /></td>
                  <td style={td}><input type="number" min={0} max={100} step="0.01" value={l.taxRatePct} disabled={frozen} onChange={(e) => updateLine(l.key, { taxRatePct: e.target.value })} style={inputStyle} /></td>
                  <td style={td}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={l.isOptional} disabled={frozen} onChange={(e) => updateLine(l.key, { isOptional: e.target.checked, selected: true })} />
                      <span style={{ fontSize: 12 }}>buyer picks</span>
                    </label>
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{moneyLabel(liveTotals.lines[i] ? liveTotals.lines[i].lineTotalCents : 0)}</td>
                  {!frozen && <td style={td}><button type="button" onClick={() => removeLine(l.key)} style={{ ...iconBtn, color: '#dc2626' }} title="Remove line">×</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!frozen && <button type="button" onClick={addLine} disabled={lines.length >= 60} style={{ ...ghostBtn, marginTop: 10 }}>+ Add line</button>}
        <div style={{ marginTop: 12, marginLeft: 'auto', maxWidth: 320, fontSize: 14, display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 16px', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ color: 'var(--room-text-muted, #64748b)' }}>Subtotal</span><span>{moneyLabel(liveTotals.subtotalCents)}</span>
          {liveTotals.discountCents > 0 && <><span style={{ color: 'var(--room-text-muted, #64748b)' }}>Discount</span><span>−{moneyLabel(liveTotals.discountCents)}</span></>}
          {liveTotals.taxCents > 0 && <><span style={{ color: 'var(--room-text-muted, #64748b)' }}>Tax</span><span>{moneyLabel(liveTotals.taxCents)}</span></>}
          <strong>Total</strong><strong>{moneyLabel(liveTotals.totalCents)}</strong>
          {liveTotals.optionalUnselectedCents > 0 && <span style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--room-text-subtle, #94a3b8)' }}>Optional lines not selected: {moneyLabel(liveTotals.optionalUnselectedCents)}</span>}
        </div>
        {linesDirty && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--room-text-subtle, #94a3b8)', textAlign: 'right' }}>Preview — save to update what the buyer sees.</div>}
      </div>

      {/* ─── E-sign hand-off ────────────────────────────────────── */}
      {proposal.status !== 'superseded' && (
        <form onSubmit={trackEnvelope} style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Contract</div>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>
            Send the contract from DocuSign as usual, then paste the envelope ID here. TrailerCast tracks its signature state on this proposal and flips the room to Won when it completes.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input type="text" value={envelopeId} onChange={(e) => setEnvelopeId(e.target.value)} placeholder="DocuSign envelope ID" maxLength={200} style={{ ...inputStyle, flex: 1, minWidth: 220 }} />
            <button type="submit" disabled={busy || !envelopeId.trim()} style={ghostBtn}>Track envelope</button>
          </div>
        </form>
      )}
    </div>
  );
}

const cardStyle = { background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 14, padding: 16, color: 'var(--room-text-strong, #0f172a)' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--room-text-body, #334155)' };
const hint = { fontWeight: 400, fontSize: 12, color: 'var(--room-text-subtle, #94a3b8)' };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #0f172a)', fontSize: 14, fontFamily: 'inherit', fontWeight: 400 };
const ghostBtn = { padding: '8px 14px', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #334155)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const primaryBtn = { padding: '9px 16px', borderRadius: 10, border: '1px solid #7c3aed', background: '#7c3aed', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const iconBtn = { width: 30, height: 30, borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #334155)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 };
const alertStyle = { padding: '10px 12px', borderRadius: 10, background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#dc2626', fontSize: 14 };
const th = { textAlign: 'left', padding: '6px 8px', fontWeight: 600 };
const td = { padding: '6px 8px', verticalAlign: 'middle' };
